"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { convertDomToMarkdown } from "@/lib/tool-kit/developer/html-to-markdown";

function convertHtmlToMarkdown(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return convertDomToMarkdown(doc.body);
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const HTML_TO_MARKDOWN_STRINGS = {
  htmlInput: "HTML input",
  inputPlaceholder:
    '<h1>Title</h1>\n<p>Some <strong>bold</strong> text with a <a href="https://example.com">link</a>.</p>',
  convertToMarkdown: "Convert to Markdown",
  copied: "Copied!",
  copyMarkdown: "Copy Markdown",
  clear: "Clear",
  markdownOutput: "Markdown output",
  htmlCharacters: "HTML characters",
  markdownCharacters: "Markdown characters",
  sizeReduction: "Size reduction",
} as const;

export default function HtmlToMarkdownWidget() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [converted, setConverted] = useState(false);
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(HTML_TO_MARKDOWN_STRINGS);

  const fmt = useToolNumber();

  function handleConvert() {
    if (input.trim().length === 0) {
      return;
    }
    setOutput(convertHtmlToMarkdown(input));
    setConverted(true);
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setConverted(false);
    setCopied(false);
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="html-input"
        >
          {strings.htmlInput}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="html-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleConvert}
          type="button"
        >
          {strings.convertToMarkdown}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyMarkdown}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Output */}
      {converted && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="md-output"
          >
            {strings.markdownOutput}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="md-output"
            readOnly
            value={output}
          />
        </div>
      )}

      {/* Stats */}
      {converted && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.htmlCharacters} value={fmt(input.length)} />
          <StatCard
            label={strings.markdownCharacters}
            value={fmt(output.length)}
          />
          <StatCard
            label={strings.sizeReduction}
            value={
              input.length === 0
                ? "0%"
                : `${Math.round(((input.length - output.length) / input.length) * 100)}%`
            }
          />
        </div>
      )}
    </>
  );
}
